<template>
  <div class="fixed inset-0 flex flex-col lg:flex-row overflow-hidden">
    <!-- Left Side - Login Form -->
    <div class="w-full lg:w-[560px] min-w-fit flex flex-col bg-[#0b1929] overflow-auto">
      <div class="w-full h-screen min-h-fit flex flex-col justify-between p-8 lg:p-10">
        <!-- Logo -->
        <div class="flex justify-center items-start">
          <img src="/images/logo-feuersoftware.svg" alt="Feuer Software" class="h-32 invert" />
        </div>

        <!-- Login Form -->
        <div class="grow flex flex-col justify-center max-w-sm mx-auto w-full">
          <div class="text-left mb-6">
            <h1 class="text-fluid-2xl font-bold text-white mb-2">
              {{ $t('login.welcomeTitle') }}<br>
              <span class="text-red-500">EinsatzMonitor</span>
            </h1>
            <p class="text-fluid-sm text-slate-400 mt-2">{{ $t('login.subtitle') }}</p>
          </div>

          <form @submit.prevent="login" class="flex flex-col gap-4">
            <div>
              <label for="username" class="block text-fluid-xs text-slate-400 mb-1.5 ml-1">{{ $t('login.username') }}</label>
              <input
                id="username"
                v-model="username"
                type="text"
                :placeholder="$t('login.username')"
                autocomplete="username"
                class="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/50 text-white placeholder-slate-500 text-fluid-base focus:outline-none focus:border-red-500/60 focus:ring-1 focus:ring-red-500/30 transition-colors"
                :class="errorMsg ? 'border-red-500/60' : ''"
              />
            </div>

            <div>
              <label for="password" class="block text-fluid-xs text-slate-400 mb-1.5 ml-1">{{ $t('login.password') }}</label>
              <div class="relative">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="$t('login.password')"
                  autocomplete="current-password"
                  class="w-full px-4 py-3 pr-12 rounded-lg bg-slate-800/60 border border-slate-700/50 text-white placeholder-slate-500 text-fluid-base focus:outline-none focus:border-red-500/60 focus:ring-1 focus:ring-red-500/30 transition-colors"
                  :class="errorMsg ? 'border-red-500/60' : ''"
                />
                <button
                  type="button"
                  tabindex="-1"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                  @click="showPassword = !showPassword"
                >
                  <Icon :name="showPassword ? 'heroicons:eye-slash-solid' : 'heroicons:eye-solid'" class="w-5 h-5" />
                </button>
              </div>
            </div>

            <p v-if="errorMsg" class="text-fluid-xs text-red-400 -mt-1">{{ errorMsg }}</p>

            <button
              type="submit"
              :disabled="loading"
              class="w-full py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-bold text-fluid-base transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Icon v-if="!loading" name="heroicons:arrow-right-end-on-rectangle" class="w-5 h-5" />
              <span v-if="loading">{{ $t('login.loggingIn') }}</span>
              <span v-else>{{ $t('login.loginButton') }}</span>
            </button>
          </form>

          <a
            href="https://connect.feuersoftware.com/v2/"
            target="_blank"
            class="text-fluid-xs text-slate-500 hover:text-slate-300 transition-colors mt-4 inline-flex items-center gap-1"
          >
            <Icon name="heroicons:arrow-right" class="w-3 h-3" />
            Connect Portal
          </a>
        </div>

        <!-- Spacer -->
        <div />
      </div>
    </div>

    <!-- Right Side - Hero Image -->
    <div class="hidden lg:flex flex-1 relative items-end justify-center">
      <img
        src="/images/login-bg.jpg"
        :alt="$t('login.heroAlt')"
        class="absolute inset-0 w-full h-full object-cover"
      />
      <div class="absolute inset-0 bg-black/40" />

      <!-- Top-right links -->
      <div class="absolute top-4 right-4 z-20 flex gap-3">
        <a
          href="https://feuersoftware.com/forum/"
          target="_blank"
          class="text-fluid-xs text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm px-3 py-2 rounded-md transition-colors"
        >
          Community
        </a>
        <a
          href="https://docs.feuersoftware.com/"
          target="_blank"
          class="text-fluid-xs text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm px-3 py-2 rounded-md transition-colors"
        >
          Dokumentation
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: false,
})

const { t } = useI18n()

const username = ref('')
const password = ref('')
const showPassword = ref(false)
const loading = ref(false)
const errorMsg = ref('')

async function login() {
  if (!username.value || !password.value) {
    errorMsg.value = t('login.errorEmpty')
    return
  }

  loading.value = true
  errorMsg.value = ''

  try {
    await $fetch('/api/auth/login', {
      method: 'POST',
      body: { username: username.value, password: password.value },
    })
    sessionStorage.setItem('just_logged_in', '1')
    await navigateTo('/')
  }
  catch (e: any) {
    errorMsg.value = e?.data?.statusMessage || t('login.errorWrongCredentials')
  }
  finally {
    loading.value = false
  }
}
</script>
